import React, { useState, useEffect } from "react";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api } from "../../utils/api";
import { useAuthStore } from "../../store/authStorage";
import toast from "react-hot-toast";

const loginSchema = z.object({
  x_id: z.string().min(1, { message: "아이디를 입력해주세요." }),
  app_key: z.string().min(1, { message: "비밀번호를  입력해주세요." }),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const { login, isAuthenticated } = useAuthStore();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      x_id: "",
      app_key: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      const response = await api.post("/admin/v1/login", data);
            
      if (response.data.status) {
        if (response.data.unmatched) {
          setError("app_key", { message: "비밀번호가 일치하지 않습니다." });
          toast.error("비밀번호가 일치하지 않습니다.");
          return;
        }
        
        login(
          {
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
          },
          response.data.data
        );
        toast.success("로그인에 성공했습니다.");
        navigate("/");
      } else {
        setError("root", { message: "로그인에 실패했습니다." });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "로그인에 실패했습니다.";
      toast.error(message);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>

      <div className="relative max-w-md w-full">
        {/* 로고 및 타이틀 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            AIvy 관리자 시스템
          </h1>
          <p className="text-gray-300">관리자 계정으로 로그인하세요</p>
        </div>

        {/* 로그인 폼 */}
        <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/20">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* 아이디 입력 */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                아이디
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register("x_id")}
                  type="text"
                  className="w-full pl-10 pr-3 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="관리자 아이디"
                />
              </div>
              {errors.x_id && (
                <p className="text-red-400 text-sm mt-2">
                  {errors.x_id.message}
                </p>
              )}
            </div>

            {/* 비밀번호 입력 */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                비밀번호
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("app_key")}
                  className="w-full pl-10 pr-10 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="비밀번호"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {errors.app_key && (
                <p className="text-red-400 text-sm mt-2">
                  {errors.app_key.message}
                </p>
              )}
            </div>

            {/* 에러 메시지 */}
            {errors.root && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                {errors.root.message}
              </div>
            )}

            {/* 로그인 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  로그인 중...
                </span>
              ) : (
                "로그인"
              )}
            </button>
          </form>

          <div className="flex items-center justify-between text-sm mt-6">
            <label className="flex items-center text-gray-300 hover:text-white cursor-pointer">
              <input
                type="checkbox"
                className="mr-2 rounded border-gray-600 bg-white/10"
              />
              로그인 상태 유지
            </label>
            <button
              type="button"
              onClick={() => {
                navigate("/auth/admin/password-reset");
              }}
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              비밀번호 찾기
            </button>
          </div>
        </div>

        {/* 푸터 */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>© 2025 AIvy. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
